import React, { createContext, useState, useContext, useMemo, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { lightTheme, darkTheme } from './index';

/**
 * Theme Context
 */
const ThemeContext = createContext({
  mode: 'light',
  toggleMode: () => {},
  setMode: () => {},
});

/**
 * Theme Provider Component
 * Wraps the app with MUI ThemeProvider and provides theme context
 */
export function ThemeProvider({ children }) {
  // Get initial mode from localStorage or default to 'light'
  // NEVER auto-detect system preference - always default to light
  const [mode, setModeState] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    // Only use saved mode if it's explicitly 'light' or 'dark'
    // Default to 'light' if nothing saved or invalid value
    return (savedMode === 'light' || savedMode === 'dark') ? savedMode : 'light';
  });

  // Update localStorage when mode changes
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
    
    // Update HTML data-theme attribute for Tailwind compatibility
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  // Toggle between light and dark
  const toggleMode = () => {
    setModeState((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // Set specific mode
  const setMode = (newMode) => {
    setModeState(newMode);
  };

  // Memoize context value
  const contextValue = useMemo(
    () => ({
      mode,
      toggleMode,
      setMode,
    }),
    [mode]
  );

  // Select theme based on mode
  const theme = mode === 'dark' ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

/**
 * Custom hook to use theme context
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}

export default ThemeContext;
